import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { streamChat } from '../utils/aiStream';
import { track } from '../telemetry';
import { isSessionError } from './useSessionGuard';
import { AI_CONVERSATIONS_KEY } from './reactQuery/useAiConversations';
import { AI_CONVERSATION_KEY } from './reactQuery/useAiConversation';
import {
  ChatMessage,
  ConversationDetail,
  DebugItem,
  ToolCallInfo,
} from '../types/IAiConversation';

// A saved tool call becomes the same debug items the live stream would have produced, so an
// old conversation renders its Query blocks exactly like a fresh one. The SQL is only there
// when the API runs in debug mode; without it there is nothing to show.
const debugFromToolCalls = (
  toolCalls?: ToolCallInfo[],
): DebugItem[] | undefined => {
  const items: DebugItem[] = [];
  (toolCalls ?? []).forEach((call) => {
    if (!call.sql) return;
    items.push({
      kind: 'tool_call',
      tool: call.toolName,
      input: JSON.stringify({ sql: call.sql }),
    });
    if (call.error) {
      items.push({
        kind: 'tool_result',
        tool: call.toolName,
        result: `ERROR: ${call.error}`,
      });
    } else if (call.rowCount != null) {
      items.push({
        kind: 'tool_result',
        tool: call.toolName,
        result: `(${call.rowCount} row${call.rowCount === 1 ? '' : 's'})`,
      });
    }
  });
  return items.length ? items : undefined;
};

const isEmptyAssistantBubble = (m: ChatMessage | undefined) =>
  m?.role === 'assistant' &&
  !m.content &&
  !m.error &&
  !m.debug?.length &&
  !m.files?.length &&
  !m.schedules?.length;

// The Ask AI conversation: the transcript, the streaming turn, and what a person can do to it.
// Everything network-side lives in streamChat; this hook only turns its events into state.
export default function useAskAiChat() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // Read inside the stream callback so the next turn picks up an adopted id.
  const conversationIdRef = useRef<number | null>(null);
  // Last user turn, so a failed turn can be re-run verbatim from the error card.
  const lastTurnRef = useRef<{ text: string } | null>(null);
  const mountedRef = useRef(true);
  const nextIdRef = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);

  const newId = (prefix: string) => {
    nextIdRef.current += 1;
    return `${prefix}-${nextIdRef.current}`;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // The in-flight stream is not aborted on unmount: it drains so the API persists the turn.
    };
  }, []);

  // Follow the answer only while the person is already near the bottom; scrolling up to read
  // must not be yanked back on every streamed delta.
  const nearBottomRef = useRef(true);
  useEffect(() => {
    const el = logRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      nearBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (nearBottomRef.current)
      logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [messages, isThinking, statusText]);

  const adoptConversationId = useCallback((id: number | null) => {
    conversationIdRef.current = id;
    setConversationId(id);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setIsThinking(false);
    setStatusText(null);
    // Drop a blank assistant bubble left by stopping before any content arrived.
    setMessages((prev) =>
      isEmptyAssistantBubble(prev[prev.length - 1]) ? prev.slice(0, -1) : prev,
    );
  }, []);

  const startNew = useCallback(() => {
    stop();
    setMessages([]);
    lastTurnRef.current = null;
    adoptConversationId(null);
  }, [stop, adoptConversationId]);

  const seedFromHistory = useCallback(
    (detail: ConversationDetail) => {
      stop();
      // Drop the retry target so Retry can never re-run the previous conversation's turn.
      lastTurnRef.current = null;
      adoptConversationId(detail.id);
      setMessages(
        (detail.messages ?? []).map((m) => ({
          id: `${detail.id}-${m.id}`,
          role: m.role?.toLowerCase() === 'user' ? 'user' : 'assistant',
          content: m.content ?? '',
          debug: debugFromToolCalls(m.toolCalls),
          files: m.files?.length ? m.files : undefined,
        })),
      );
    },
    [stop, adoptConversationId],
  );

  // Shared streaming core for both a fresh send and a retry.
  const runTurn = useCallback(
    async (text: string, assistantId: string, isRetry: boolean) => {
      const turnStartedAt = performance.now();
      const endpoint =
        conversationIdRef.current != null
          ? 'api/ai/conversations/{id}/chat'
          : 'api/ai/conversations/chat';
      let toolCalls = 0;
      let filesCreated = 0;
      let hadError = false;

      track('ai_message_sent', {
        messageLength: text.length,
        isRetry,
        hasConversation: conversationIdRef.current != null,
      });

      setIsStreaming(true);
      setIsThinking(true);
      setStatusText(null);

      const controller = new AbortController();
      abortRef.current = controller;

      const updateAssistant = (fn: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? fn(m) : m)),
        );

      const finish = () => {
        // The turn is persisted server-side by now, so the saved copy of the conversation is
        // stale and the list needs its title. Done here, not in the page, so it also happens
        // when the person navigated away and let the stream drain.
        const turnConversationId = conversationIdRef.current;
        if (turnConversationId != null)
          queryClient.removeQueries([AI_CONVERSATION_KEY, turnConversationId]);
        queryClient.invalidateQueries([AI_CONVERSATIONS_KEY]);
        if (mountedRef.current)
          updateAssistant((m) =>
            m.streaming ? { ...m, streaming: false } : m,
          );
        track('ai_response_received', {
          latencyMs: Math.round(performance.now() - turnStartedAt),
          toolCalls,
          filesCreated,
          hadError,
          aborted: abortRef.current !== controller,
        });
        // Only finalise if no newer turn replaced this controller.
        if (abortRef.current === controller) {
          abortRef.current = null;
          if (mountedRef.current) {
            setIsStreaming(false);
            setIsThinking(false);
            setStatusText(null);
          }
        }
      };

      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (error) {
        hadError = true;
        if (isSessionError(error)) {
          // The session guard only watches react-query; a raw fetch turn has to do this itself.
          loginWithRedirect({ appState: { returnTo: `${pathname}${search}` } });
        } else if (mountedRef.current) {
          updateAssistant((m) => ({
            ...m,
            error: {
              message: 'Could not sign the request. Please try again.',
              kind: 'server',
            },
          }));
        }
        finish();
        return;
      }

      streamChat({
        token,
        message: text,
        conversationId: conversationIdRef.current,
        signal: controller.signal,
        onEvent: (event) => {
          // Page left mid-stream: let it drain server-side, apply nothing client-side.
          if (!mountedRef.current) return;
          switch (event.type) {
            case 'conversation':
              adoptConversationId(event.conversationId);
              break;
            case 'status':
              if (event.message) setStatusText(event.message);
              break;
            case 'text':
              setIsThinking(false);
              setStatusText(null);
              if (event.text)
                updateAssistant((m) => ({
                  ...m,
                  content: m.content + event.text,
                }));
              break;
            case 'textReset':
              updateAssistant((m) => ({ ...m, content: '' }));
              break;
            case 'debug':
              if (event.debug.kind === 'tool_call') toolCalls += 1;
              updateAssistant((m) => ({
                ...m,
                debug: [...(m.debug ?? []), event.debug],
              }));
              break;
            case 'refresh':
              if (event.schedules.length) {
                updateAssistant((m) => ({
                  ...m,
                  schedules: [...(m.schedules ?? []), ...event.schedules],
                }));
              }
              if (event.files.length) {
                filesCreated += event.files.length;
                updateAssistant((m) => ({
                  ...m,
                  files: [
                    ...(m.files ?? []),
                    ...event.files.filter(
                      (f) => !m.files?.some((x) => x.id === f.id),
                    ),
                  ],
                }));
              }
              break;
            case 'error':
              setIsThinking(false);
              hadError = true;
              track('ai_stream_error', {
                kind: event.kind,
                status: event.status,
              });
              if (event.kind === 'http' || event.kind === 'network') {
                // Keeps the api_error dashboards complete: axios never sees this request.
                track('api_error', {
                  endpoint,
                  method: 'POST',
                  status: event.status ?? 0,
                  durationMs: Math.round(performance.now() - turnStartedAt),
                  kind: event.kind === 'http' ? 'http' : 'network',
                });
              }
              if (event.status === 503) {
                // Not configured on this environment: a notice, not an error card.
                setUnavailable(event.message);
                // The shared status query is the other source of truth; let it re-ask the API.
                queryClient.invalidateQueries(['ai-status']);
                setMessages((prev) =>
                  isEmptyAssistantBubble(prev[prev.length - 1])
                    ? prev.slice(0, -1)
                    : prev,
                );
              } else {
                // Keep any partial answer; surface the failure as its own card.
                updateAssistant((m) => ({
                  ...m,
                  error: {
                    message: event.message,
                    kind: event.kind,
                    status: event.status,
                  },
                }));
              }
              break;
            case 'done':
              setIsThinking(false);
              setStatusText(null);
              break;
            default:
              break;
          }
        },
      }).finally(finish);
    },
    [
      getAccessTokenSilently,
      loginWithRedirect,
      pathname,
      search,
      adoptConversationId,
      queryClient,
    ],
  );

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text || abortRef.current || unavailable) return;

      lastTurnRef.current = { text };
      const assistantId = newId('a');
      setMessages((prev) => [
        ...prev,
        { id: newId('u'), role: 'user', content: text },
        { id: assistantId, role: 'assistant', content: '', streaming: true },
      ]);
      runTurn(text, assistantId, false);
    },
    [runTurn, unavailable],
  );

  // Drop the failed assistant bubble and re-stream the same question.
  const retry = useCallback(() => {
    const last = lastTurnRef.current;
    if (!last || abortRef.current) return;

    const assistantId = newId('a');
    setMessages((prev) => {
      const next = [...prev];
      if (next[next.length - 1]?.role === 'assistant') next.pop();
      return [
        ...next,
        { id: assistantId, role: 'assistant', content: '', streaming: true },
      ];
    });
    runTurn(last.text, assistantId, true);
  }, [runTurn]);

  return {
    messages,
    conversationId,
    isStreaming,
    isThinking,
    statusText,
    unavailable,
    canRetry: lastTurnRef.current != null && !isStreaming,
    logRef,
    send,
    retry,
    stop,
    startNew,
    seedFromHistory,
  };
}
